import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, ArrowRight, Menu, X, ChevronDown,
  Target, Share2, Monitor, Search, Video, Bot, Headphones, Sparkles, MessageSquare,
  ShoppingBag, Laptop, Heart, Briefcase, Home as HomeIcon, TrendingUp,
  BookOpen, Plane, Factory, Scale, Car, UtensilsCrossed
} from 'lucide-react';
import { services, industries } from '../mock';

const serviceIconMap = {
  Target, Share2, Monitor, Search, Video, Bot, Headphones, Sparkles, MessageSquare
};

const industryIconMap = {
  ShoppingBag, Laptop, Heart, Briefcase, Home: HomeIcon, TrendingUp,
  BookOpen, Plane, Factory, Scale, Car, UtensilsCrossed
};

const Header = ({ transparent = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const servicesRef = useRef(null);
  const industriesRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setServicesOpen(false);
      }
      if (industriesRef.current && !industriesRef.current.contains(event.target)) {
        setIndustriesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setMobileMenuOpen(false);
    setServicesOpen(false);
    setIndustriesOpen(false);
  };

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      // Already on homepage, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Navigate to homepage (ScrollToTop component will handle scrolling)
      navigate('/');
    }
    setMobileMenuOpen(false);
    setServicesOpen(false);
    setIndustriesOpen(false);
  };

  const navigateToService = (slug) => {
    navigate(`/services/${slug}`);
    setServicesOpen(false);
    setMobileMenuOpen(false);
  };

  const navigateToIndustry = (slug) => {
    navigate(`/industries/${slug}`);
    setIndustriesOpen(false);
    setMobileMenuOpen(false);
  };

  const ServiceIcon = ({ iconName }) => {
    const Icon = serviceIconMap[iconName] || Target;
    return <Icon size={18} />;
  };

  const IndustryIcon = ({ iconName }) => {
    const Icon = industryIconMap[iconName] || Briefcase;
    return <Icon size={18} />;
  };

  const dropdownVariants = {
    hidden: { opacity: 0, y: 10, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { duration: 0.2, ease: 'easeOut' }
    },
    exit: { 
      opacity: 0, 
      y: 10, 
      scale: 0.95,
      transition: { duration: 0.15 }
    }
  };

  return (
    <motion.nav 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-4 left-0 right-0 z-50 px-4"
    >
      <div className={`max-w-7xl mx-auto backdrop-blur-xl border rounded-full px-6 py-3 transition-all duration-300 ${
        scrolled ? 'bg-surface/90 border-brand/30 shadow-lg shadow-brand/10' : 'bg-surface/80 border-brand/20'
      }`}>
        <div className="flex justify-between items-center">
          {/* Logo */}
          <motion.div 
            className="flex items-center space-x-3 cursor-pointer"
            whileHover={{ scale: 1.05 }}
            onClick={handleLogoClick}
          >
            <div className="w-12 h-12 rounded-xl overflow-hidden">
              <img 
                src="/logo.png" 
                alt="RANSEN" 
                className="w-full h-full object-contain logo-motion"
              />
            </div>
            <div>
              <div className="text-xl font-heading font-bold text-white tracking-tight">RANSEN</div>
              <div className="text-[10px] text-white/60 tracking-wider uppercase">AI-Driven Growth Agency</div>
            </div>
          </motion.div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2">
            <button 
              onClick={() => navigate('/')} 
              className="text-sm font-medium text-white/70 hover:text-white transition-colors px-4 py-2"
            >
              Home
            </button>

            {/* Services Dropdown */}
            <div className="relative" ref={servicesRef}>
              <button 
                onClick={() => {
                  setServicesOpen(!servicesOpen);
                  setIndustriesOpen(false);
                }}
                className={`flex items-center gap-1 text-sm font-medium transition-colors px-4 py-2 rounded-full ${
                  servicesOpen ? 'text-white bg-white/10' : 'text-white/70 hover:text-white'
                }`}
              >
                Services
                <ChevronDown 
                  size={16} 
                  className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[700px] bg-surface/95 backdrop-blur-xl border border-brand/20 rounded-2xl shadow-2xl shadow-brand/20 overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
                        <h3 className="font-heading font-bold text-white">Our Services</h3>
                        <button 
                          onClick={() => scrollToSection('services')}
                          className="text-xs text-brand hover:text-brand/80 flex items-center gap-1"
                        >
                          View All <ArrowRight size={12} />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2">
                        {services.map((service) => (
                          <motion.button
                            key={service.id}
                            onClick={() => navigateToService(service.slug)}
                            whileHover={{ scale: 1.02, backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
                            className="flex items-start gap-3 p-3 rounded-xl text-left transition-all hover:bg-brand/5 group"
                          >
                            <div className="w-10 h-10 bg-gradient-to-br from-brand/20 to-brand/5 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:from-brand/30 group-hover:to-brand/10 transition-all">
                              <ServiceIcon iconName={service.icon} />
                            </div>
                            <div className="min-w-0">
                              <h4 className="font-medium text-white text-sm leading-tight mb-1 group-hover:text-brand transition-colors">
                                {service.title}
                              </h4>
                              <p className="text-white/50 text-xs line-clamp-2 leading-relaxed">
                                {service.description.substring(0, 60)}...
                              </p>
                            </div>
                          </motion.button>
                        ))}
                      </div>

                      {/* Featured Services Banner */}
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <div className="flex gap-4">
                          <div className="flex-1 bg-gradient-to-r from-brand/20 to-accent-pink/10 rounded-xl p-4 border border-brand/20">
                            <div className="flex items-center gap-2 mb-2">
                              <Target className="text-brand" size={16} />
                              <span className="text-xs font-semibold text-brand uppercase tracking-wider">Featured</span>
                            </div>
                            <h4 className="font-heading font-bold text-white text-sm mb-1">Paid Media Advertising</h4>
                            <p className="text-white/60 text-xs">15+ ad platforms, 8 industry strategies</p>
                          </div>
                          <div className="flex-1 bg-gradient-to-r from-accent-cyan/20 to-brand/10 rounded-xl p-4 border border-accent-cyan/20">
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles className="text-accent-cyan" size={16} />
                              <span className="text-xs font-semibold text-accent-cyan uppercase tracking-wider">New</span>
                            </div>
                            <h4 className="font-heading font-bold text-white text-sm mb-1">LLMO & AEO Optimization</h4>
                            <p className="text-white/60 text-xs">AI-first search optimization</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Industries Dropdown */}
            <div className="relative" ref={industriesRef}>
              <button 
                onClick={() => {
                  setIndustriesOpen(!industriesOpen);
                  setServicesOpen(false);
                }}
                className={`flex items-center gap-1 text-sm font-medium transition-colors px-4 py-2 rounded-full ${
                  industriesOpen ? 'text-white bg-white/10' : 'text-white/70 hover:text-white'
                }`}
              >
                Industries
                <ChevronDown 
                  size={16} 
                  className={`transition-transform duration-200 ${industriesOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence>
                {industriesOpen && (
                  <motion.div
                    variants={dropdownVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="absolute top-full right-0 mt-4 w-[700px] max-w-[calc(100vw-2rem)] bg-surface/95 backdrop-blur-xl border border-brand/20 rounded-2xl shadow-2xl shadow-brand/20 overflow-hidden"
                  >
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
                        <h3 className="font-heading font-bold text-white">Industries We Serve</h3>
                        <button 
                          onClick={() => scrollToSection('industries')}
                          className="text-xs text-brand hover:text-brand/80 flex items-center gap-1"
                        >
                          View All <ArrowRight size={12} />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2">
                        {industries.map((industry) => (
                          <motion.button
                            key={industry.id}
                            onClick={() => navigateToIndustry(industry.slug)}
                            whileHover={{ scale: 1.02, backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
                            className="flex items-center gap-3 p-3 rounded-xl text-left transition-all hover:bg-brand/5 group"
                          >
                            <div className="w-9 h-9 bg-gradient-to-br from-accent-cyan/20 to-brand/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:from-accent-cyan/30 group-hover:to-brand/20 transition-all">
                              <IndustryIcon iconName={industry.icon} />
                            </div>
                            <div className="min-w-0">
                              <h4 className="font-medium text-white text-sm leading-tight group-hover:text-accent-cyan transition-colors truncate">
                                {industry.name}
                              </h4>
                            </div>
                          </motion.button>
                        ))}
                      </div>

                      {/* Industry Stats */}
                      <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-heading font-bold text-brand">12+</div>
                          <div className="text-xs text-white/60">Industries</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-heading font-bold text-accent-cyan">500+</div>
                          <div className="text-xs text-white/60">Projects Delivered</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-heading font-bold text-accent-pink">98%</div>
                          <div className="text-xs text-white/60">Client Satisfaction</div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button 
              onClick={() => navigate('/story')} 
              className="text-sm font-medium text-white/70 hover:text-white transition-colors px-4 py-2"
            >
              Story
            </button>

            <button 
              onClick={() => navigate('/results')} 
              className="text-sm font-medium text-white/70 hover:text-white transition-colors px-4 py-2"
            >
              Results
            </button>

            <button 
              onClick={() => navigate('/blog')} 
              className="text-sm font-medium text-white/70 hover:text-white transition-colors px-4 py-2"
            >
              Blog
            </button>

            <button 
              onClick={() => scrollToSection('contact')} 
              className="text-sm font-medium text-white/70 hover:text-white transition-colors px-4 py-2"
            >
              Contact
            </button>
          </div>
          
          {/* CTA Button */}
          <div className="hidden lg:flex items-center">
            <motion.button
              onClick={() => scrollToSection('contact')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-gray-900 hover:bg-gray-100 rounded-full px-5 py-2 font-semibold shadow-lg flex items-center transition-all text-sm whitespace-nowrap"
            >
              Get Started
              <ArrowRight className="ml-2" size={14} />
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="pt-4 pb-4 space-y-1 border-t border-white/10 mt-4">
                <button 
                  onClick={() => navigate('/')} 
                  className="block w-full text-left px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 rounded-lg"
                >
                  Home
                </button>

                {/* Mobile Services Accordion */}
                <div>
                  <button 
                    onClick={() => setServicesOpen(!servicesOpen)}
                    className="flex items-center justify-between w-full px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 rounded-lg"
                  >
                    <span>Services</span>
                    <ChevronDown 
                      size={16} 
                      className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  <AnimatePresence>
                    {servicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="pl-4 space-y-1 overflow-hidden"
                      >
                        {services.map((service) => (
                          <button
                            key={service.id}
                            onClick={() => navigateToService(service.slug)}
                            className="flex items-center gap-3 w-full px-4 py-2 text-white/60 hover:text-white text-sm text-left rounded-lg hover:bg-white/5"
                          >
                            <ServiceIcon iconName={service.icon} />
                            <span>{service.title}</span>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Mobile Industries Accordion */}
                <div>
                  <button 
                    onClick={() => setIndustriesOpen(!industriesOpen)}
                    className="flex items-center justify-between w-full px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 rounded-lg"
                  >
                    <span>Industries</span>
                    <ChevronDown 
                      size={16} 
                      className={`transition-transform duration-200 ${industriesOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  <AnimatePresence>
                    {industriesOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="pl-4 space-y-1 overflow-hidden"
                      >
                        {industries.map((industry) => (
                          <button
                            key={industry.id}
                            onClick={() => navigateToIndustry(industry.slug)}
                            className="flex items-center gap-3 w-full px-4 py-2 text-white/60 hover:text-white text-sm text-left rounded-lg hover:bg-white/5"
                          >
                            <IndustryIcon iconName={industry.icon} />
                            <span>{industry.name}</span>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <button 
                  onClick={() => { navigate('/story'); setMobileMenuOpen(false); }} 
                  className="block w-full text-left px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 rounded-lg"
                >
                  Story
                </button>
                <button 
                  onClick={() => { navigate('/results'); setMobileMenuOpen(false); }} 
                  className="block w-full text-left px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 rounded-lg"
                >
                  Results
                </button>
                <button 
                  onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} 
                  className="block w-full text-left px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 rounded-lg"
                >
                  Blog
                </button>
                <button 
                  onClick={() => scrollToSection('contact')} 
                  className="block w-full text-left px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 rounded-lg"
                >
                  Contact
                </button>

                {/* Mobile CTA */}
                <div className="pt-4 px-4">
                  <button
                    onClick={() => scrollToSection('contact')}
                    className="w-full bg-gradient-to-r from-brand-dark to-brand text-white rounded-full px-5 py-3 font-semibold flex items-center justify-center"
                  >
                    Get Started
                    <ArrowRight className="ml-2" size={16} />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Header;
